import Vue from 'vue';
import Vuex from 'vuex';
import createLogger from 'vuex/dist/logger';

import axios from 'axios';
import { setApiAdapterOptions } from '@src/api/adapter';

import apiApp from '@src/api/app';
import apiDatabases from '@src/api/databases';
import apiDocuments from '@src/api/documents';

import auth from './auth';
import search from './search';
import documents from './document';
import home from './home';
import dictionary from './dictionary';
import notifications from './notifications';

import { DB_EXPERT_DOMAIN_ID } from '@src/api/databases';
import {
  DOCUMENT_STATUS_ACTIVE,
  DOCUMENT_STATUS_DISABLED,
  DOCUMENT_STATUS_PARTLY,
  DOCUMENT_STATUS_PROJECT,
  DOCUMENT_STATUS_UNDEFINED
} from '@src/api/documents';

Vue.use(Vuex);

const SERVER_VERSION = '4.0.27.236'

const statusIcons = {
  [DOCUMENT_STATUS_ACTIVE]: '#status--active',
  [DOCUMENT_STATUS_DISABLED]: '#status--disabled',
  [DOCUMENT_STATUS_PARTLY]: '#status--partly',
  [DOCUMENT_STATUS_PROJECT]: '#status--project',
  [DOCUMENT_STATUS_UNDEFINED]: '#status--na'
};

const checkInternetAvailability = checkImageUrl => {
  return new Promise(resolve => {
    let img = new Image();
    img.src = checkImageUrl + '?' + new Date().getTime();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
};

/**
 * @property {string} apiUrl
 * @property {number} apiTimeout
 * @property {string} checkInternetImageUrl
 * @property {string} normacsProjectsUrl
 * @property {string} normacsDiscussionsUrl
 * @property {string} normacsNtdsUrl
 * @property {string} normacsNewsUrl
 */
class AppConfig {
  constructor(options) {
    Object.assign(this, options);
  }
}

export default new Vuex.Store({
  modules: {
    auth,
    search,
    documents,
    home,
    dictionary,
    notifications
  },
  strict: __DEVELOPMENT__,
  plugins: __DEVELOPMENT__ ? [createLogger()] : [],

  state: {
    // Признак инициализации начальных данных хранилища.
    // Значение true, если загружены данные config, appInfo и documentStatuses.
    initialized: false,
    loading: false,
    loadingError: null,    
    config: null,       // @type {AppConfig|null}
    appInfo: null,      // @type {AppConfig|null}
    databases: {},
    databaseIds: {},
    dbExpertId: null,   // @type {string|null} Идентификатор базы "Ответы экспертов"
    documentStatuses: null,
    hasInternet: false,
    windowSize: window.innerWidth
  },

  getters: {
    getWindowSize: state => state.windowSize,
    hasInternet: state => state.hasInternet,
    statusIcon: state => id => state.documentStatuses[id].icon,
    currDatabase:  state => id => state.databases[id]
  },

  actions: {
    async initApplication({ commit, dispatch }) {
      commit('SET_LOADING', true);

      try{
        let res = await axios.get(`${__BASE_URL__}config.json?${new Date().getTime()}`)
        let config = new AppConfig(res.data)

        setApiAdapterOptions(config.apiUrl, config.apiTimeout)
        dispatch('checkInternet', config.checkInternetImageUrl)  // ???: AWAIT ???
        let appInfoPromise = apiApp.getInfo()
        let statusesPromise = apiDocuments.getStatuses()
        let appInfo = await appInfoPromise
        let statuses = await statusesPromise

        // ----------       Проверка версии сервера       ------------
        let needServVer = SERVER_VERSION.split('.').map(i => +i)
        let realServVer = appInfo.version.split('.').map(i => +i)
        let verIsGood = true
        let len = needServVer.length

        for(let i = 0; i < len; i++) {
          if(realServVer[i] > needServVer[i]) break
          if(realServVer[i] < needServVer[i]) {
            verIsGood = false
            break
          }
        }
        if(!verIsGood) throw new Error('Необходимо обновить сервер. Обратитесь к администратору'); 
        // -----------------------------------------------------------

        Object.keys(statuses).forEach(id => {
          statuses[id] = {
            id: id,
            name: statuses[id],
            icon: statusIcons[id]
          };
        })

        commit('INIT_APPLICATION', {
          config,
          appInfo: appInfo,
          documentStatuses: statuses
        });
        commit('SET_LOADING', false);
      } catch(err){        
        // Обработка специальной ошибки на axios.get(`${__BASE_URL__}...
        if (err.response && err.response.status === 404) {
          err = new Error('Configuration file not found.');
        }
        // Обработка специальной ошибки на apiApp.getInfo - недоступность API
        if (err.response && err.response.data && err.response.data.errorCode === 0) {
          err = new Error(err.response.data.errorMessage);
        }
        commit('SET_LOADING', err)
        throw err
      }
    },
    checkInternet({ commit }, checkImageUrl) {
      checkInternetAvailability(checkImageUrl).then(isAvailable => {
        commit('HAS_INTERNET', isAvailable);
      });
    },
    setWindowSize({ commit, state }, value) {
      commit('UPDATE_WINDOW_SIZE', value);
    },
    async loadDatabases({ commit }) {
      commit('SET_LOADING', true);

      try {
        let databases = await apiDatabases.getDatabases()

        commit('SET_DATABASES', databases)
        commit('SET_LOADING', false)
      } catch(err){
        commit('SET_LOADING', err)
      }
    }
  },

  mutations: {
    UPDATE_WINDOW_SIZE(state, value) {
      state.windowSize = value;
    },
    INIT_APPLICATION(state, { config, appInfo, documentStatuses }) {
      state.config = config;
      state.appInfo = appInfo;
      state.documentStatuses = documentStatuses;
      state.initialized = true;
    },
    SET_LOADING(state, value) {
      if (value === true) {
        state.loading = true;
        state.loadingError = null;
      } else {
        state.loading = false;
        if (value instanceof Error) {
          state.loadingError = value;
        }
      }
    },
    SET_DATABASES(state, databases) {
      state.databases = databases;
      Object.keys(databases).forEach(id => {
        let domainId = databases[id].domainId;
        state.databaseIds[domainId] = id;

        if (domainId === DB_EXPERT_DOMAIN_ID) {
          state.dbExpertId = id;
        }
      });
    },
    UNSET_DATABASES(state) {
      state.databases = {};
      state.databaseIds = {};
      state.dbExpertId = null;
    },
    HAS_INTERNET(state, value) {
      state.hasInternet = value;
    }
  }
});
